import { isDeepEqual } from '@attain-apps/data-utils'

import type { Feature } from './feature'
import type * as models from './models'

export class Selectors {
  public readonly orgCharts

  public readonly getOrgChart
  public readonly getOrgChartData
  public readonly getNodes
  public readonly getNode
  public readonly getEdges
  public readonly getEditingNode

  public readonly canUndo
  public readonly canRedo

  public readonly hasProposal
  public readonly hasProposedChange

  constructor(feature: Feature) {
    this.orgCharts = feature.state.project(state => state.orgCharts)

    this.getOrgChart = feature.createSelectorMethod(
      ({ orgChartId }: models.OrgChart.Identifier) =>
        this.orgCharts.project(orgCharts => orgCharts[orgChartId]),
    )
    this.getOrgChartData = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChart(identifier).project(
          orgChart =>
            orgChart &&
            ({
              // NOTE: the entire purpose of this is to focus on just the core data
              // for more efficient change detection
              edges: orgChart.edges,
              nodes: orgChart.nodes,
            } satisfies models.Data),
        ),
    )
    this.getNodes = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChartData(identifier).project(data => data?.nodes ?? []),
    )
    this.getNode = feature.createSelectorMethod(
      (params: models.OrgChart.Identifier & models.Node.Identifier) =>
        this.getNodes(params).project(nodes =>
          nodes.find(node => node.id === params.nodeId),
        ),
    )
    this.getEdges = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChartData(identifier).project(data => data?.edges ?? []),
    )
    this.getEditingNode = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChart(identifier).project(orgChart => {
          const nodeId = orgChart?.currentlyEditingNodeId
          return nodeId ? orgChart?.edits[nodeId] : undefined
        }),
    )

    this.canUndo = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChart(identifier).project(
          orgChart =>
            !!orgChart && orgChart.history.length - orgChart.undoCount > 0,
        ),
    )
    this.canRedo = feature.createSelectorMethod(
      (identifier: models.OrgChart.Identifier) =>
        this.getOrgChart(identifier).project(
          orgChart => !!orgChart && orgChart.undoCount > 0,
        ),
    )

    this.hasProposal = this.orgCharts.project(orgCharts => !!orgCharts.proposed)
    this.hasProposedChange = feature.createSelectorMethod(
      ({ nodeId }: models.Node.Identifier) =>
        this.getNode({ nodeId, orgChartId: 'current' }).project(
          this.getNode({ nodeId, orgChartId: 'proposed' }).asSelector,
          (currentNode, proposedNode) =>
            !isDeepEqual(currentNode, proposedNode),
        ),
    )
  }
}
